import React, { Component, Fragment } from 'react';
import defaultTheme from '@lightspeed/cirrus-tokens/dist/theme/default';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { TERMINAL_TYPE } from 'shared_modules/Terminals';
import {
  TerminalEventMonitoring,
  EVENT_TYPES,
} from 'app/src/payments/terminals/PaymentServiceTerminal';
import StripeTerminalService from 'app/src/payments/terminals/StripeTerminalService';

import { actionTypes } from 'pages/store/actions/defaults';
import { postData, putData, deleteData } from 'pages/store/actions/data';
import { entities } from 'pages/store/schema';
import { getValue, getProcessing, getErrors } from 'pages/store/selectors';
import { showLegacyApiErrors } from 'pages/utils/errors';

import concat from 'lodash/concat';
import get from 'lodash/get';

import { Button } from '@lightspeed/flame/Button';
import Card, { CardSection } from '@lightspeed/cirrus-card';
import { Divider } from '@lightspeed/flame/Divider';
import Dialog from '@lightspeed/cirrus-dialog';
import { IconShop } from '@lightspeed/flame/Icon/Shop';
import Text from '@lightspeed/cirrus-text';
import { wait } from 'pages/utils/wait';

import CCTerminal from 'pages/views/settings/PaymentProcessing/gateway/LightspeedPayments/CCTerminal';
import AddCCTerminal from 'pages/views/settings/PaymentProcessing/gateway/LightspeedPayments/CCTerminal/Add';
import EditCCTerminal from 'pages/views/settings/PaymentProcessing/gateway/LightspeedPayments/CCTerminal/Edit';
import TestCCTerminal from 'pages/views/settings/PaymentProcessing/gateway/LightspeedPayments/CCTerminal/Test';

export const STATE = {
  VIEW: {
    ADD: 'add',
    EDIT: 'edit',
    DELETE: 'delete',
    TEST: 'test',
  },
  TEST: {
    SUCCESS: 'success',
    FAIL: 'error',
    IN_PROGRESS: 'in-progress',
  },
};

const merchantos = window.merchantos;
const iconSize = '1.125rem';
const getAutorizedShopsIds = authorizedShops => {
  try {
    return get(JSON.parse(authorizedShops), `retail.${merchantos.account.id}`, []);
  } catch (e) {
    showLegacyApiErrors(e);
    return [];
  }
};

const connectToStripeTerminal = async (gatewayParameters, serialNumber) => {
  try {
    if (!gatewayParameters) {
      throw new Error(`Missing Gateway Parameters: ${gatewayParameters}`);
    }

    if (!serialNumber) {
      throw new Error(`Missing serialNumber: ${serialNumber}`);
    }

    const StripeTerminalServiceInstance = await StripeTerminalService.getInstance();
    const { discoveredReaders } =
      (await StripeTerminalServiceInstance.discoverReaders(gatewayParameters)) || [];

    const selectedReader = discoveredReaders.filter(
      reader => reader.serial_number === serialNumber
    );

    if (selectedReader && selectedReader.length > 0) {
      // Initializing the connection here allow the terminal to register with Stripe Server
      const connectedReader = await StripeTerminalServiceInstance.connectReader(selectedReader[0]);

      // We make the connection last 2 seconds
      await wait(2000);

      // We cut the connection here
      StripeTerminalServiceInstance.disconnectReader();

      return connectedReader;
    }

    throw new Error(`Unable to find the newly paired reader`);
  } catch (e) {
    throw e;
  }
};

export class GatewayConfigComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      viewState: null,
      terminalId: null,
      testStatus: null,
      testErrorMessage: null,
      pairingPayload: {},
    };

    this.handleUpdate = this.handleUpdate.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleRetryPairing = this.handleRetryPairing.bind(this);
    this.handleConnectionTest = this.handleConnectionTest.bind(this);
    this.handleCloseTest = this.handleCloseTest.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { ccTerminalErrors } = this.props;

    if (!prevProps.ccTerminalErrors && ccTerminalErrors) {
      const { response: { status } = {} } = ccTerminalErrors;

      if (status < 400 || status > 422) {
        /* eslint-disable */
        this.setState({ viewState: null });
        /* eslint-enable */
        showLegacyApiErrors(ccTerminalErrors);
      }
    }
  }

  handleDelete() {
    const { terminalId } = this.state;

    this.props.deleteData(
      `/API/Account/${merchantos.account.id}/CCTerminal/${terminalId}.json`,
      terminalId,
      () => {
        this.setState({ viewState: null });
      }
    );
  }

  handleUpdate(terminalParameters) {
    // We list here the parameters keys that we should not
    // send along with an update on the CCTerminal endpoint
    const NON_EDITABLE_PARAMETERS = ['configuration'];

    const { terminalId } = this.state;

    const filteredTerminalParameters = Object.keys(terminalParameters)
      .filter(key => !NON_EDITABLE_PARAMETERS.includes(key))
      .reduce((accumulator, currentValue) => {
        return { ...accumulator, [currentValue]: terminalParameters[currentValue] };
      }, {});

    this.props.putData(
      `/API/Account/${merchantos.account.id}/CCTerminal/${terminalId}.json`,
      filteredTerminalParameters,
      () => {
        this.setState({ viewState: null });
      }
    );
  }

  async handleConnectionTest(ccTerminal) {
    const { gatewayConfig } = this.props;

    const type = get(ccTerminal, 'type', null);
    const ccTerminalID = get(ccTerminal, 'ccTerminalID', null);
    const gatewayConfigID = get(ccTerminal, 'gatewayConfigID', null);
    const serialNumber = get(ccTerminal, 'configuration.serialNumber', null);
    const gatewayConfigParameters = get(gatewayConfig(gatewayConfigID), 'parameters', null);
    const connectionTestMonitoring = new TerminalEventMonitoring(
      ccTerminal,
      'TerminalConnectionTest'
    );

    try {
      switch (type) {
        case TERMINAL_TYPE.LIGHTSPEED_P400_PLUS_STRIPE:
        case TERMINAL_TYPE.LIGHTSPEED_BBPOS_WISEPOSE_STRIPE:
          connectionTestMonitoring.submitEvent(EVENT_TYPES.PAYMENT.TERMINAL.CONNECTING);

          this.setState({
            viewState: STATE.VIEW.TEST,
            testStatus: STATE.TEST.IN_PROGRESS,
            terminalId: ccTerminalID,
          });

          const onConnectionSuccess = await connectToStripeTerminal(
            gatewayConfigParameters,
            serialNumber
          );

          if (onConnectionSuccess) {
            connectionTestMonitoring.submitEvent(EVENT_TYPES.PAYMENT.TERMINAL.CONNECTED);
            this.setState({
              testStatus: STATE.TEST.SUCCESS,
            });
          } else {
            throw new Error(`Cannot connect to the terminal.`);
          }

          break;
        default:
          // No test available for the terminal model we close the modal
          this.setState({ viewState: null });
      }
    } catch (e) {
      connectionTestMonitoring.submitError(e);

      this.props.deleteData(
        `/API/Account/${merchantos.account.id}/CCTerminal/${ccTerminalID}.json`,
        ccTerminalID,
        () => {
          this.setState({ testErrorMessage: e.message, testStatus: STATE.TEST.FAIL });
        }
      );
    }
  }

  handleSubmit(pairingPayload) {
    // We store the pairingPayload in case of retry
    this.setState({ pairingPayload });

    this.props.postData(
      `/API/Account/${merchantos.account.id}/CCTerminal.json`,
      pairingPayload,
      ({ CCTerminal: ccTerminalData } = {}) => {
        this.handleConnectionTest(ccTerminalData);
      }
    );
  }

  handleRetryPairing() {
    const { pairingPayload } = this.state;

    this.setState(
      {
        viewState: STATE.VIEW.TEST,
        testStatus: STATE.TEST.IN_PROGRESS,
        testErrorMessage: null,
      },
      async () => {
        const transitionDurationBase = parseInt(
          get(defaultTheme, 'transition.transition-duration-base', 200),
          10
        );

        // We wait to let the fading animation complete,
        // before re-submitting the payload to avoid flashes
        await wait(transitionDurationBase);

        this.handleSubmit(pairingPayload);
      }
    );
  }

  handleCloseTest() {
    this.setState(
      {
        viewState: null,
      },
      async () => {
        const transitionDurationBase = parseInt(
          get(defaultTheme, 'transition.transition-duration-base', 200),
          10
        );

        // We wait to let the fading animation complete,
        // before resetting the rest of states
        await wait(transitionDurationBase);

        this.setState({ testStatus: null, testErrorMessage: null, pairingPayload: {} });
      }
    );
  }

  render() {
    const {
      ccTerminal,
      ccTerminalErrors,
      address,
      authorizedShops,
      gatewayConfigID,
      shops,
      isCCTerminalProcessing,
      isCCTerminalDeleting,
      supportedTerminalTypes,
    } = this.props;
    const { line1, line2, line3, city, region, country, postalCode } = address;
    const { viewState, terminalId, testStatus, testErrorMessage } = this.state;

    return (
      <Fragment key={gatewayConfigID}>
        <Card large>
          <CardSection>
            <div className="cr-flex cr-flex-wrap">
              <div className="cr-flex cr-width-full cr-width-6-12-md">
                <div className="cr-flex cr-flex-col">
                  <div className="cr-flex cr-items-center">
                    <div className="cr-mr-2" style={{ width: iconSize }}>
                      <IconShop color="blue" size={iconSize} />
                    </div>
                    <Text heading3>
                      {getAutorizedShopsIds(authorizedShops)
                        .map(id => get(shops(id), 'name', 'n/a'))
                        .join(', ')}
                    </Text>
                  </div>
                  <div className="cr-flex cr-items-center">
                    <div className="cr-mr-2" style={{ minWidth: iconSize, height: iconSize }} />
                    {address && (
                      <div>
                        {line1 && (
                          <Text bodySmall dimmed>
                            {line1}
                          </Text>
                        )}
                        {line2 && (
                          <Text bodySmall dimmed>
                            {line2}
                          </Text>
                        )}
                        {line3 && (
                          <Text bodySmall dimmed>
                            {line3}
                          </Text>
                        )}
                        <Text bodySmall dimmed>
                          {city && `${city}, `}
                          {region && `${region}, `}
                          {country && `${country}, `}
                          {postalCode && `${postalCode}`}
                        </Text>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="cr-flex cr-width-full cr-width-6-12-md cr-mt-3 cr-pl-2 cr-justify-end cr-items-end">
                <Button
                  data-test="retail-payments-ccterminal-add"
                  variant="secondary"
                  onClick={() => {
                    this.setState({ viewState: STATE.VIEW.ADD });
                  }}
                >
                  Add a Payment Terminal
                </Button>
              </div>
            </div>
          </CardSection>
          <Divider />
          <CardSection noSpacing>
            <CCTerminal
              gatewayConfigId={gatewayConfigID}
              onEdit={ccTerminalId => {
                this.setState({ viewState: STATE.VIEW.EDIT, terminalId: ccTerminalId });
              }}
              onDelete={ccTerminalId => {
                this.setState({ viewState: STATE.VIEW.DELETE, terminalId: ccTerminalId });
              }}
            />
          </CardSection>
        </Card>
        <div className="cr-mb-2" />
        <Dialog
          isOpen={viewState === STATE.VIEW.DELETE}
          isLoading={isCCTerminalDeleting}
          type="danger"
          className="cirrus"
          title="Unpair Terminal"
          message={`Are you sure you want to unpair ${get(
            ccTerminal(terminalId),
            'name',
            'this terminal'
          )}?`}
          confirmText="Unpair"
          onCancel={
            /* istanbul ignore next */ () => {
              this.setState({ viewState: null });
            }
          }
          onConfirm={this.handleDelete}
        />
        <AddCCTerminal
          isOpen={viewState === STATE.VIEW.ADD}
          isProcessing={isCCTerminalProcessing}
          ccTerminalErrors={ccTerminalErrors ? concat(ccTerminalErrors) : []}
          gatewayConfigID={gatewayConfigID}
          supportedTerminalTypes={supportedTerminalTypes}
          onCancel={() => {
            this.setState({ viewState: null });
          }}
          onSuccess={this.handleSubmit}
        />
        <EditCCTerminal
          isOpen={viewState === STATE.VIEW.EDIT}
          isProcessing={isCCTerminalProcessing}
          ccTerminal={ccTerminal(terminalId)}
          ccTerminalErrors={ccTerminalErrors ? concat(ccTerminalErrors) : []}
          onCancel={() => {
            this.setState({ viewState: null });
          }}
          onSuccess={this.handleUpdate}
        />
        <TestCCTerminal
          isOpen={viewState === STATE.VIEW.TEST}
          isProcessing={isCCTerminalDeleting}
          errorMessage={testErrorMessage}
          status={testStatus}
          onRetry={this.handleRetryPairing}
          onSuccess={this.handleCloseTest}
        />
      </Fragment>
    );
  }
}

GatewayConfigComponent.propTypes = {
  address: PropTypes.shape(PropTypes.any.object),
  authorizedShops: PropTypes.string,
  gatewayConfigID: PropTypes.string,
  shops: PropTypes.func,
  supportedTerminalTypes: PropTypes.arrayOf(PropTypes.string),
};

const mapStateToProps = state => ({
  shops: id => getValue(entities.shop, id)(state),
  ccTerminal: id => getValue(entities.ccTerminal, id)(state),
  gatewayConfig: id => getValue(entities.gatewayConfig, id)(state),
  ccTerminalErrors: getErrors({ entity: entities.ccTerminal, actionType: actionTypes.SAVING })(
    state
  ),
  isCCTerminalProcessing: getProcessing({
    entity: entities.ccTerminal,
    list: false,
    actionType: actionTypes.SAVING,
  })(state),
  isCCTerminalDeleting: getProcessing({
    entity: entities.ccTerminal,
    list: false,
    actionType: actionTypes.DELETING,
  })(state),
});

const mapDispatchToProps = dispatch => ({
  postData: (url, data, onSuccess) =>
    dispatch(postData({ url, payload: data, entity: entities.ccTerminal, onSuccess })),
  putData: (url, data, onSuccess) =>
    dispatch(putData({ url, payload: data, entity: entities.ccTerminal, onSuccess })),
  deleteData: (url, id, onSuccess) =>
    dispatch(deleteData({ url, id, entity: entities.ccTerminal, onSuccess })),
});

export default connect(mapStateToProps, mapDispatchToProps)(GatewayConfigComponent);
